import React, { useEffect, useState } from "react";
import { Save, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";

const AdminSettings = () => {
  const [guildId, setGuildId] = useState("");
  const [autoJoin, setAutoJoin] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((res) => {
        setGuildId(res.data.guild_id || "");
        setAutoJoin(res.data.auto_join !== false);
        setLoaded(true);
      })
      .catch(() => {
        toast.error("Failed to load settings");
        setLoaded(true);
      });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { guild_id: guildId, auto_join: autoJoin });
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="glass-card py-14 flex justify-center">
        <p className="text-[#bcd9f2]">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="glass-card p-5 sm:p-8 max-w-2xl">
      <h3 className="font-display font-bold text-white text-[1.2rem]">Discord auto-join</h3>
      <p className="mt-1.5 text-[#9fc5e8] text-[0.88rem] leading-relaxed">
        When someone logs in with Discord, the bot automatically adds them to this server.
      </p>

      <div className="mt-7 space-y-6">
        <div>
          <p className="text-[#9fc5e8] text-[0.68rem] font-bold tracking-[0.2em] uppercase">Server (Guild) ID</p>
          <Input
            value={guildId}
            onChange={(e) => setGuildId(e.target.value)}
            placeholder="e.g. 1524574133663563777"
            className="mt-2 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-[#7fd0f5]/40 h-11 rounded-xl font-mono"
          />
        </div>

        <div className="flex items-center justify-between glass-card-inner px-5 py-4">
          <div>
            <p className="text-white font-bold text-[0.95rem]">Auto-join enabled</p>
            <p className="text-[#9fc5e8] text-[0.8rem] mt-0.5">Add users to the server right after login</p>
          </div>
          <Switch checked={autoJoin} onCheckedChange={setAutoJoin} className="data-[state=checked]:bg-[#53fc18]/70" />
        </div>

        <div className="flex items-start gap-3 rounded-xl border border-yellow-300/25 bg-yellow-300/8 px-4 py-3.5">
          <Bot className="w-4 h-4 text-yellow-200 mt-0.5 shrink-0" />
          <p className="text-yellow-100/90 text-[0.8rem] leading-relaxed">
            The bot must be a member of this server with the <strong>Create Invite</strong> permission, otherwise auto-join will fail silently for new users.
          </p>
        </div>

        <button type="button" onClick={save} disabled={saving} className="btn-pill btn-yellow !py-3 !px-7 text-[0.75rem] disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save settings
        </button>
      </div>
    </div>
  );
};

export default AdminSettings;
